import { Component, createEffect, createMemo, createSignal, For, onMount, Show } from 'solid-js'
import { galleryStore } from '/web/store/gallery'
import { userStore } from '/web/store/user'
import { getAssetUrl } from '/web/shared/util'
import { characterStore } from '/web/store'
import { AppSchema } from '/common/types'
import { GalleryImage } from '/common/types/aifans-schema'
import { Title, Meta, Link } from '@solidjs/meta'
import { useInfiniteScroll } from '/web/shared/hooks/useInfiniteScroll'
import { getCanonicalUrl } from '/web/shared/aifans/lib/config-util'
import FlagIcon from '/web/shared/FlagIcon'
import TrashIcon from '/web/shared/TrashIcon'
import Footer from '/web/shared/aifans/Footer'

const Gallery: Component = () => {
  const [selectedImage, setSelectedImage] = createSignal<GalleryImage | undefined>(undefined)
  const [charactersInFilter, setCharactersInFilter] = createSignal<AppSchema.Character[]>([])
  const [selectedCharacterFilter, setSelectedCharacterFilter] = createSignal<string | undefined>(
    undefined
  )
  const [search, setSearch] = createSignal('')

  const gallery = galleryStore((s) => ({
    images: s.images,
    loading: s.loading,
    showAllImages: s.showAllImages,
    showFlaggedImages: s.showFlaggedImages,
    hasMore: s.hasMore,
  }))

  const user = userStore((s) => ({
    user: s.user,
  }))

  const chars = characterStore((s) => ({
    list: s.characters.list,
    map: s.characters.list.reduce<Record<string, AppSchema.Character>>(
      (prev, curr) => Object.assign(prev, { [curr._id]: curr }),
      {}
    ),
    loaded: s.characters.loaded,
  }))

  const handleToggleShowAll = async () => {
    galleryStore.toggleShowAllImages()
    await galleryStore.initPaged()
  }

  const handleToggleShowFlagged = async () => {
    galleryStore.toggleShowFlaggedImages()
    await galleryStore.initPaged()
  }

  const handleFlagImage = async (imageId: string, flagReason?: string) => {
    await galleryStore.flagImage(imageId, flagReason)
  }

  const handleUnflagImage = async (imageId: string) => {
    await galleryStore.unflagImage(imageId)
  }

  const handleDeleteImage = async (imageId: string) => {
    if (confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      await galleryStore.deleteImage(imageId)
    }
  }

  onMount(async () => {
    if (!gallery.loading) {
      await galleryStore.initPaged()
    }
    if (!chars.loaded) {
      await characterStore.getCharacters()
    }
  })

  const loadMore = () => {
    if (!gallery.loading && gallery.hasMore) {
      galleryStore.loadMore()
    }
  }

  const { setTarget } = useInfiniteScroll(loadMore)

  const filteredImages = createMemo(() => {
    let imgs =
      gallery?.images?.filter((image) => {
        if (gallery.showFlaggedImages) {
          if (!image.flagged) return false
        } else {
          if (image.flagged) return false
        }
        if (selectedCharacterFilter() && image.characterId !== selectedCharacterFilter()) {
          return false
        }
        return true
      }) || []

    const q = search().toLowerCase().trim()
    if (q) {
      imgs = imgs.filter((image) => {
        const char = chars.map[image.characterId]
        return char?.name?.toLowerCase().includes(q) || image.prompt?.toLowerCase().includes(q)
      })
    }

    return imgs
  })

  createEffect(() => {
    if (gallery.images?.length) {
      const characterIdCounts = gallery.images.reduce<Record<string, number>>((acc, image) => {
        acc[image.characterId] = (acc[image.characterId] || 0) + 1
        return acc
      }, {})

      const characterData = chars.list
        .map((character) => ({
          ...character,
          count: characterIdCounts[character._id] || 0,
        }))
        .filter((character) => character.count > 0)

      setCharactersInFilter(characterData)
    }
  })

  return (
    <>
      <Title>CosplayFans - Gallery | Explore Stunning Cosplay Creations</Title>
      <Meta name="og:title" content="CosplayFans - Gallery" />
      <Meta
        name="description"
        content="View your personal collection of AI-generated cosplay images. Access and manage all your custom character creations in one place."
      />
      <Meta
        name="og:description"
        content="View your personal collection of AI-generated cosplay images. Access and manage all your custom character creations in one place."
      />
      <Link rel="canonical" href={getCanonicalUrl('/gallery/')} />

      <div style={{ padding: '32px clamp(16px, 3vw, 40px) 56px', 'max-width': '1400px', margin: '0 auto' }}>
        {/* ── Hero ── */}
        <div class="pb-8 pt-6 text-center">
          <div
            class="mb-[10px] inline-flex items-center gap-2 rounded-full px-[14px] py-[6px] font-rajdhani text-[11px] font-bold uppercase tracking-[1.8px] text-[#FF3CAC]"
            style={{ background: 'rgba(138,43,226,0.12)', border: '1px solid rgba(138,43,226,0.3)' }}
          >
            <span class="h-[6px] w-[6px] rounded-full bg-[var(--cf-cyan)] shadow-[0_0_8px_var(--cf-cyan)]" />
            My Collection
          </div>
          <h1
            class="mb-4 font-bebas leading-[0.98] text-white"
            style={{ 'font-size': 'clamp(36px, 5.5vw, 72px)' }}
          >
            My <span class="cf-grad-text">Gallery</span>
          </h1>
          <p
            class="mx-auto mb-7 max-w-[580px] text-[var(--cf-muted)]"
            style={{ 'font-size': 'clamp(14px, 1.1vw, 18px)', 'line-height': '1.65' }}
          >
            All your AI-generated cosplay images in one place. Browse, download, and manage your
            creations.
          </p>
          <div class="inline-flex items-center gap-5 rounded-full border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.03)] px-7 py-[14px] backdrop-blur-[10px]">
            <div class="flex flex-col items-center gap-[2px]">
              <span class="cf-grad-text font-bebas text-[28px] leading-none tracking-[1px]">
                {filteredImages().length}
              </span>
              <span class="font-rajdhani text-[10px] font-bold uppercase tracking-[1.6px] text-[var(--cf-muted)]">
                Images
              </span>
            </div>
            <div class="h-7 w-px bg-[rgba(255,255,255,0.1)]" />
            <div class="flex flex-col items-center gap-[2px]">
              <span class="cf-grad-text font-bebas text-[28px] leading-none tracking-[1px]">
                {charactersInFilter().length}
              </span>
              <span class="font-rajdhani text-[10px] font-bold uppercase tracking-[1.6px] text-[var(--cf-muted)]">
                Characters
              </span>
            </div>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div class="mb-6 flex flex-wrap items-center gap-3">
          {/* Search */}
          <div class="relative min-w-[260px] flex-1">
            <svg
              class="pointer-events-none absolute left-[14px] top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-[rgba(255,255,255,0.5)]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search your gallery..."
              class="h-[44px] w-full rounded-[12px] border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.04)] pl-[42px] pr-[14px] font-nunito text-[14px] text-white outline-none transition-colors duration-200 placeholder:text-[rgba(255,255,255,0.35)] focus:border-[rgba(0,229,255,0.4)]"
              value={search()}
              onInput={(e) => setSearch(e.currentTarget.value)}
            />
          </div>

          {/* Character filters */}
          <div class="flex flex-wrap gap-[6px]">
            <button
              class={`h-[44px] cursor-pointer rounded-[12px] px-[18px] font-rajdhani text-[12px] font-bold tracking-[0.5px] transition-all duration-150 ${
                !selectedCharacterFilter()
                  ? 'border border-transparent text-white shadow-[0_4px_14px_rgba(138,43,226,0.35)]'
                  : 'border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.04)] text-[rgba(255,255,255,0.7)] hover:border-[rgba(138,43,226,0.4)] hover:bg-[rgba(138,43,226,0.15)] hover:text-white'
              }`}
              style={
                !selectedCharacterFilter()
                  ? { background: 'var(--cf-grad-hot)' }
                  : {}
              }
              onClick={() => setSelectedCharacterFilter(undefined)}
            >
              All
            </button>
            <For each={charactersInFilter().sort((a, b) => a.name.localeCompare(b.name))}>
              {(c: any) => (
                <button
                  class={`h-[44px] cursor-pointer rounded-[12px] px-[18px] font-rajdhani text-[12px] font-bold tracking-[0.5px] transition-all duration-150 ${
                    selectedCharacterFilter() === c._id
                      ? 'border border-transparent text-white shadow-[0_4px_14px_rgba(138,43,226,0.35)]'
                      : 'border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.04)] text-[rgba(255,255,255,0.7)] hover:border-[rgba(138,43,226,0.4)] hover:bg-[rgba(138,43,226,0.15)] hover:text-white'
                  }`}
                  style={
                    selectedCharacterFilter() === c._id
                      ? { background: 'var(--cf-grad-hot)' }
                      : {}
                  }
                  onClick={() => setSelectedCharacterFilter(c._id)}
                >
                  {c.name}
                </button>
              )}
            </For>

            {/* Admin filters */}
            <Show when={user.user?.admin}>
              <div class="h-[44px] w-px bg-[rgba(255,255,255,0.1)]" />
              <button
                class={`h-[44px] cursor-pointer rounded-[12px] px-[18px] font-rajdhani text-[12px] font-bold tracking-[0.5px] transition-all duration-150 ${
                  gallery.showAllImages
                    ? 'border border-transparent bg-[rgba(0,229,255,0.2)] text-white'
                    : 'border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.04)] text-[rgba(255,255,255,0.7)] hover:text-white'
                }`}
                onClick={handleToggleShowAll}
              >
                {gallery.showAllImages ? 'All Users' : 'My Images'}
              </button>
              <button
                class={`h-[44px] cursor-pointer rounded-[12px] px-[18px] font-rajdhani text-[12px] font-bold tracking-[0.5px] transition-all duration-150 ${
                  gallery.showFlaggedImages
                    ? 'border border-transparent bg-[rgba(220,38,38,0.3)] text-white'
                    : 'border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.04)] text-[rgba(255,255,255,0.7)] hover:text-white'
                }`}
                onClick={handleToggleShowFlagged}
              >
                Flagged
              </button>
            </Show>
          </div>
        </div>

        {/* ── Grid ── */}
        <Show when={filteredImages().length > 0}>
          <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <For each={filteredImages()}>
              {(image) => {
                const char = () => chars.map[image.characterId]
                return (
                  <div
                    class="group relative cursor-pointer overflow-hidden rounded-[16px] border border-[var(--cf-hairline)] bg-[var(--cf-surface)] transition-all duration-[250ms] hover:translate-y-[-4px] hover:border-[rgba(0,229,255,0.35)] hover:shadow-[0_16px_36px_rgba(0,229,255,0.18)]"
                    onClick={() => setSelectedImage(image)}
                  >
                    <div class="relative aspect-[3/4] overflow-hidden">
                      <img
                        class="h-full w-full object-cover transition-transform duration-[450ms] group-hover:scale-[1.06]"
                        style={{ 'object-position': 'center 18%', 'transition-timing-function': 'cubic-bezier(.2,.9,.3,1)' }}
                        src={getAssetUrl(image.url)}
                        alt={char()?.name || 'Gallery image'}
                      />

                      {/* Flagged badge (admin-only) */}
                      <Show when={user.user?.admin && image.flagged}>
                        <div class="absolute left-[10px] top-[10px] z-[2] rounded-full bg-red-500 px-[10px] py-1 font-rajdhani text-[10px] font-bold uppercase tracking-[1px] text-white">
                          Flagged
                        </div>
                      </Show>

                      {/* Hover action buttons */}
                      <div class="absolute right-[10px] top-[10px] z-[3] flex gap-[5px] opacity-0 transition-opacity duration-[250ms] group-hover:opacity-100">
                        <Show when={user.user?.admin}>
                          <button
                            class="grid h-8 w-8 place-items-center rounded-full border border-[rgba(255,255,255,0.18)] bg-[rgba(8,6,16,0.85)] text-white backdrop-blur-[8px] transition-all duration-150 hover:scale-[1.08] hover:border-[rgba(0,229,255,0.5)] hover:bg-[rgba(0,229,255,0.25)]"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (image.flagged) {
                                handleUnflagImage(image._id)
                              } else {
                                const reason = prompt('Flag reason (optional):')
                                if (reason !== null) {
                                  handleFlagImage(image._id, reason || undefined)
                                }
                              }
                            }}
                            title={image.flagged ? 'Unflag image' : 'Flag image'}
                          >
                            <FlagIcon size={13} />
                          </button>
                          <Show when={image.flagged}>
                            <button
                              class="grid h-8 w-8 place-items-center rounded-full border border-[rgba(255,255,255,0.18)] bg-[rgba(8,6,16,0.85)] text-white backdrop-blur-[8px] transition-all duration-150 hover:scale-[1.08] hover:border-[rgba(220,38,38,0.6)] hover:bg-[rgba(220,38,38,0.4)]"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteImage(image._id)
                              }}
                              title="Delete image"
                            >
                              <TrashIcon size={13} />
                            </button>
                          </Show>
                        </Show>
                      </div>

                      {/* Character name + date overlay */}
                      <div class="pointer-events-none absolute bottom-0 left-0 right-0 px-3 pb-[10px] pt-10" style={{ background: 'linear-gradient(180deg, transparent, rgba(8,6,16,0.92))' }}>
                        <Show when={char()}>
                          <div class="font-bebas text-[18px] leading-[1.1] tracking-[0.5px] text-white">
                            {char()!.name}
                          </div>
                        </Show>
                        <Show when={image.createdAt}>
                          <div class="mt-[3px] font-rajdhani text-[11px] font-semibold text-[rgba(255,255,255,0.7)]">
                            {new Date(image.createdAt!).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </div>
                        </Show>
                      </div>
                    </div>
                  </div>
                )
              }}
            </For>

            <Show when={gallery.hasMore}>
              <div class="col-span-full flex justify-center py-4">
                <div ref={setTarget as any} />
                <button
                  onClick={loadMore}
                  class="h-[44px] rounded-[12px] border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.04)] px-6 font-rajdhani text-[13px] font-bold text-white transition-all duration-150 hover:border-[rgba(0,229,255,0.4)] hover:bg-[rgba(0,229,255,0.05)]"
                >
                  {gallery.loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            </Show>
          </div>
        </Show>

        {/* ── Empty state ── */}
        <Show when={filteredImages().length === 0 && !gallery.loading}>
          <div class="rounded-[20px] border border-dashed border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.02)] px-5 py-16 text-center">
            <div class="mb-[14px] text-[56px] opacity-50">🖼️</div>
            <h3 class="mb-2 font-bebas text-[28px] tracking-[1px] text-white">No images yet</h3>
            <p class="mb-5 text-[var(--cf-muted)]">
              {search() || selectedCharacterFilter()
                ? 'No images match your filters. Try adjusting your search.'
                : 'Generate your first cosplay image to start building your gallery.'}
            </p>
            <Show when={!search() && !selectedCharacterFilter()}>
              <a
                href="/generate-image"
                class="inline-flex h-[44px] items-center gap-2 rounded-full px-6 font-rajdhani text-[13px] font-bold uppercase tracking-[0.8px] text-white transition-all duration-200 hover:translate-y-[-2px]"
                style={{ background: 'var(--cf-grad-hot)', 'box-shadow': '0 4px 14px rgba(138,43,226,0.35)' }}
              >
                Generate Image
              </a>
            </Show>
          </div>
        </Show>

        <Show when={gallery.loading && filteredImages().length === 0}>
          <div class="py-16 text-center font-rajdhani text-[14px] text-[var(--cf-muted)]">
            Loading your gallery...
          </div>
        </Show>
      </div>

      {/* ── Lightbox ── */}
      <Show when={selectedImage()}>
        <div
          class="fixed inset-0 z-[1000] flex items-center justify-center p-5"
          style={{ background: 'rgba(0,0,0,0.92)', 'backdrop-filter': 'blur(14px)' }}
          onClick={() => setSelectedImage(undefined)}
        >
          <button
            class="fixed right-[18px] top-[18px] z-[1002] grid h-10 w-10 place-items-center rounded-full border border-[rgba(255,255,255,0.2)] bg-[rgba(255,255,255,0.08)] text-[26px] leading-none text-white transition-all duration-200 hover:rotate-90 hover:bg-[rgba(255,60,172,0.3)]"
            onClick={() => setSelectedImage(undefined)}
          >
            ×
          </button>
          <div
            class="flex w-full max-w-[1100px] flex-col gap-6 overflow-hidden rounded-[22px] border border-[rgba(138,43,226,0.3)] p-6 md:flex-row md:h-[720px]"
            style={{
              background: 'linear-gradient(180deg, #14101f, #0c0916)',
              'max-height': 'calc(100vh - 60px)',
              'box-shadow': '0 32px 80px rgba(138,43,226,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image */}
            <div class="flex flex-1 items-center justify-center overflow-hidden rounded-[14px] bg-black md:aspect-auto md:flex-none md:w-[540px]" style={{ 'aspect-ratio': '3/4' }}>
              <img
                class="h-full w-full object-cover"
                style={{ 'object-position': 'center 18%' }}
                src={getAssetUrl(selectedImage()!.url)}
                alt={chars.map[selectedImage()!.characterId]?.name}
              />
            </div>

            {/* Info panel */}
            <div class="flex w-full max-w-[360px] flex-col gap-[14px]">
              <div
                class="inline-flex items-center gap-2 self-start rounded-full px-[14px] py-[6px] font-rajdhani text-[11px] font-bold uppercase tracking-[1.8px] text-[#FF3CAC]"
                style={{ background: 'rgba(138,43,226,0.12)', border: '1px solid rgba(138,43,226,0.3)' }}
              >
                <span class="h-[6px] w-[6px] rounded-full bg-[var(--cf-cyan)] shadow-[0_0_8px_var(--cf-cyan)]" />
                AI Generated
              </div>

              <h2 class="font-bebas text-[32px] tracking-[1px] text-white">
                {chars.map[selectedImage()!.characterId]?.name || 'Unknown'}
              </h2>

              <Show when={chars.map[selectedImage()!.characterId]?.realName}>
                <div class="-mt-2 font-rajdhani text-[14px] font-semibold text-[var(--cf-muted)]">
                  by {chars.map[selectedImage()!.characterId]?.realName}
                </div>
              </Show>

              {/* Meta */}
              <div class="flex flex-col gap-[10px] rounded-[12px] border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.03)] px-4 py-[14px]">
                <Show when={selectedImage()!.createdAt}>
                  <div class="flex justify-between font-rajdhani text-[13px]">
                    <span class="text-[11px] font-bold uppercase tracking-[1.2px] text-[var(--cf-muted)]">
                      Created
                    </span>
                    <span class="font-semibold text-white">
                      {new Date(selectedImage()!.createdAt!).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                </Show>
                <Show when={selectedImage()!.prompt}>
                  <div class="flex flex-col gap-1">
                    <span class="font-rajdhani text-[11px] font-bold uppercase tracking-[1.2px] text-[var(--cf-muted)]">
                      Prompt
                    </span>
                    <span class="font-nunito text-[13px] leading-[1.5] text-[rgba(255,255,255,0.8)]">
                      {selectedImage()!.prompt}
                    </span>
                  </div>
                </Show>
              </div>

              {/* Actions */}
              <div class="mt-auto flex flex-col gap-2">
                <a
                  href={getAssetUrl(selectedImage()!.url)}
                  target="_blank"
                  rel="noopener"
                  class="flex h-[44px] items-center justify-center gap-2 rounded-[12px] font-rajdhani text-[13px] font-bold uppercase tracking-[0.8px] text-white transition-all duration-200 hover:translate-y-[-2px]"
                  style={{ background: 'var(--cf-grad-hot)', 'box-shadow': '0 4px 14px rgba(138,43,226,0.35)' }}
                >
                  ⬇ Download Full Size
                </a>
                <button
                  class="flex h-[44px] items-center justify-center gap-2 rounded-[12px] border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.04)] font-rajdhani text-[13px] font-bold uppercase tracking-[0.8px] text-white transition-all duration-150 hover:border-[rgba(0,229,255,0.4)] hover:bg-[rgba(0,229,255,0.05)]"
                  onClick={() => setSelectedImage(undefined)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </Show>

      <Footer />
    </>
  )
}

export default Gallery
